import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Activity, Award } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatList({ title, items, icon: Icon, accent }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent }} />}
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{title}</span>
      </div>
      <ul className="text-xs space-y-0.5 ml-0.5">
        {items.map((s, i) => (
          <li key={i} className="text-gray-700">
            {s}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AFLTeamBlock({ side, accentColor }) {
  if (!side) return null;
  const goalkickers = side.goalkickers || [];
  const top = side.top_performers || [];
  return (
    <div className="flex-1 min-w-0">
      <div className="text-xs font-bold uppercase tracking-wider truncate mb-2" style={{ color: accentColor }}>
        {side.team}
      </div>
      <div className="space-y-3">
        {goalkickers.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Award className="w-3.5 h-3.5 text-[#FFB81C]" />
              <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Goalkickers</span>
            </div>
            <ul className="text-xs space-y-0.5">
              {goalkickers.map((g, i) => (
                <li key={i} className="text-gray-700 flex items-center justify-between">
                  <span className="truncate">{g.name}</span>
                  <span className="font-bold tabular-nums text-[#1A1A1A] ml-2">{g.goals}.{g.behinds}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {top.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Top performers</div>
            <ul className="text-xs space-y-0.5">
              {top.map((p, i) => (
                <li key={i} className="flex items-center justify-between gap-2">
                  <span className="text-gray-700 truncate">{p.name}</span>
                  <span className="text-gray-500 text-[11px] flex-shrink-0">
                    {p.disposals}d
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function NRLStatBlock({ side, accentColor }) {
  if (!side) return null;
  return (
    <div className="flex-1 min-w-0">
      <div className="text-xs font-bold uppercase tracking-wider truncate mb-2" style={{ color: accentColor }}>
        {side.team}
      </div>
      <div className="space-y-3">
        <StatList title="Tries" items={side.tries} />
        <StatList title="Conversions" items={side.conversions} />
        {side.penalty_goals?.length > 0 && (
          <StatList title="Penalty Goals" items={side.penalty_goals} />
        )}
        {side.field_goals?.length > 0 && (
          <StatList title="Field Goals" items={side.field_goals} />
        )}
      </div>
    </div>
  );
}

function EPLStatBar({ label, homeVal, awayVal, format = 'int', unit = '' }) {
  if (homeVal == null && awayVal == null) return null;
  const h = homeVal ?? 0;
  const a = awayVal ?? 0;
  const total = (h || 0) + (a || 0);
  // Percentage split for the visual bar. For possession we already have the
  // real split (h ≈ 100 - a) so use it directly.
  let homePct = 50;
  if (format === 'pct') {
    homePct = h > 0 || a > 0 ? (h / (h + a)) * 100 : 50;
  } else if (total > 0) {
    homePct = (h / total) * 100;
  }
  const fmt = (v) => {
    if (v == null) return '—';
    if (format === 'float') return (Math.round(v * 100) / 100).toFixed(2);
    if (format === 'pct') return `${v}${unit || '%'}`;
    return `${v}${unit || ''}`;
  };
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-bold text-[#1A1A1A] tabular-nums">{fmt(homeVal)}</span>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</span>
        <span className="font-bold text-[#1A1A1A] tabular-nums">{fmt(awayVal)}</span>
      </div>
      <div className="flex h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className="bg-[#37003C] transition-all" style={{ width: `${homePct}%` }} />
        <div className="bg-[#00FF87] transition-all" style={{ width: `${100 - homePct}%` }} />
      </div>
    </div>
  );
}

export default function MatchLiveStats({ sport, homeTeam, awayTeam, roundNumber, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Auto-fetch when defaultOpen=true so stats are visible without a click
  useEffect(() => {
    if (!defaultOpen || hasFetched) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/match-stats`, {
          params: { sport, home_team: homeTeam, away_team: awayTeam, round_number: roundNumber },
          withCredentials: true,
        });
        if (!cancelled) setStats(res.data.stats || {});
      } catch {
        if (!cancelled) setStats({});
      }
      if (!cancelled) {
        setHasFetched(true);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [defaultOpen, hasFetched, sport, homeTeam, awayTeam, roundNumber]);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !hasFetched) {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/match-stats`, {
          params: { sport, home_team: homeTeam, away_team: awayTeam, round_number: roundNumber },
          withCredentials: true,
        });
        setStats(res.data.stats || {});
      } catch {
        setStats({});
      }
      setHasFetched(true);
      setLoading(false);
    }
  };

  const aflGks = stats?.leading_goalkickers || [];
  const aflHome = stats?.home;
  const aflAway = stats?.away;
  const hasAflLive = sport === 'AFL' && (aflHome?.goalkickers?.length || aflAway?.goalkickers?.length);
  const hasAflStats = sport === 'AFL' && (aflGks.length > 0 || hasAflLive);
  const hasNrlStats = sport === 'NRL' && (stats?.home || stats?.away);
  // EPL has a meaningful stats payload only when at least one metric is
  // populated for either side (ESPN sometimes returns the boxscore shell
  // with all-null fields for pre-match fixtures).
  const eplMetrics = ['shots', 'shots_on_target', 'possession', 'xg', 'corners'];
  const hasEplStats = sport === 'EPL' && stats && eplMetrics.some(k =>
    stats.home?.[k] != null || stats.away?.[k] != null);

  return (
    <div className="mt-2 border-t border-gray-100 pt-2" data-testid={`live-stats-${sport}`}>
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between text-[11px] uppercase tracking-widest text-gray-500 hover:text-[#1A1A1A] font-semibold transition-colors"
        data-testid="live-stats-toggle"
      >
        <span className="flex items-center gap-1.5">
          <Activity className="w-3 h-3" />
          Game Stats
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="mt-3 bg-gray-50 rounded-sm p-3" data-testid="live-stats-panel">
          {loading ? (
            <div className="text-center py-2 text-xs text-gray-400">Loading…</div>
          ) : sport === 'AFL' ? (
            hasAflStats ? (
              <div className="space-y-3">
                {aflGks.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Award className="w-3.5 h-3.5 text-[#FFB81C]" />
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Leading Goalkicker(s) (Admin)</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {aflGks.map((g, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 bg-yellow-100 text-[#8B6914] text-xs font-semibold px-2 py-1 rounded-sm"
                        >
                          <Award className="w-3 h-3 fill-current" />
                          {g}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {hasAflLive && (
                  <div className="flex gap-4">
                    <AFLTeamBlock side={aflHome} accentColor="#E00000" />
                    <div className="w-px bg-gray-200 self-stretch" />
                    <AFLTeamBlock side={aflAway} accentColor="#E00000" />
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400">
                Match stats aren't available yet. They'll appear here once the AFL stats feed releases them.
              </p>
            )
          ) : hasNrlStats ? (
            <div className="flex gap-4">
              <NRLStatBlock side={stats.home} accentColor="#007A33" />
              <div className="w-px bg-gray-200 self-stretch" />
              <NRLStatBlock side={stats.away} accentColor="#007A33" />
            </div>
          ) : sport === 'EPL' ? (
            hasEplStats ? (
              <div>
                {/* Team labels */}
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold mb-3">
                  <span className="text-[#37003C] truncate max-w-[45%]">{stats.home?.team || homeTeam}</span>
                  <span className="text-gray-400">vs</span>
                  <span className="text-[#00A550] truncate max-w-[45%] text-right">{stats.away?.team || awayTeam}</span>
                </div>
                <EPLStatBar label="Possession" homeVal={stats.home?.possession} awayVal={stats.away?.possession} format="pct" />
                <EPLStatBar label="xG" homeVal={stats.home?.xg} awayVal={stats.away?.xg} format="float" />
                <EPLStatBar label="Shots" homeVal={stats.home?.shots} awayVal={stats.away?.shots} />
                <EPLStatBar label="Shots on Target" homeVal={stats.home?.shots_on_target} awayVal={stats.away?.shots_on_target} />
                <EPLStatBar label="Corners" homeVal={stats.home?.corners} awayVal={stats.away?.corners} />
                {(stats.home?.fouls != null || stats.away?.fouls != null) && (
                  <EPLStatBar label="Fouls" homeVal={stats.home?.fouls} awayVal={stats.away?.fouls} />
                )}
                {(stats.home?.yellow_cards != null || stats.away?.yellow_cards != null) && (
                  <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-2 h-3 bg-[#FFCC00] rounded-sm" />
                      <span className="tabular-nums">{stats.home?.yellow_cards ?? 0}</span>
                      {(stats.home?.red_cards || 0) > 0 && (
                        <>
                          <span className="inline-block w-2 h-3 bg-[#E00000] rounded-sm ml-1" />
                          <span className="tabular-nums">{stats.home.red_cards}</span>
                        </>
                      )}
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Cards</span>
                    <div className="flex items-center gap-2">
                      {(stats.away?.red_cards || 0) > 0 && (
                        <>
                          <span className="tabular-nums">{stats.away.red_cards}</span>
                          <span className="inline-block w-2 h-3 bg-[#E00000] rounded-sm mr-1" />
                        </>
                      )}
                      <span className="tabular-nums">{stats.away?.yellow_cards ?? 0}</span>
                      <span className="inline-block w-2 h-3 bg-[#FFCC00] rounded-sm" />
                    </div>
                  </div>
                )}
                {stats.source && (
                  <div className="text-[9px] text-gray-400 mt-2 text-right uppercase tracking-widest">
                    via {stats.source}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400">Match stats aren't available yet — they'll appear here once the game kicks off.</p>
            )
          ) : (
            <p className="text-xs text-gray-400">No stats available for this match yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
